import { IModule } from "angular";
import { LookupManagementServiceProvider } from "./provider";
import { StateParams, StateProvider, StateService } from "@uirouter/angularjs";
import { LookupManagementConfig, LookupManagementService } from "./service";
import { GridSettingsService } from "../../grids/settings/gridSettingsService";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../server/serverUtility";
import { ServiceResponse } from "../../server/query";
import { ReferenceRelationshipCollection } from "../../server/references";
import {
  FormViewConfig,
  FormViewController,
  FormViewService,
  vsServiceName as FormViewServiceName,
  vsComponentName as FormViewComponentName,
} from "../../forms/formView";

import { vsComponentName as lookupManagementComponentName } from "./component";
import {
  GridConfiguration,
  vsComponentName as RecordGridViewComponentName,
} from "../../views/recordGridView";
import { vsComponentName as LookupDetailComponentName } from "./Details";
import fieldHelpers from "../../../ui-formly/fieldHelpers";

export interface LookupManagementServiceProviderConfig {
  lookupManagementServiceProviderName: string;
  lookupManagementServiceName: string;
  baseStateSref: string;
}

export function configureLookupManagement(
  module: IModule,
  config: LookupManagementServiceProviderConfig
) {
  module.provider(config.lookupManagementServiceName, [
    LookupManagementServiceProvider,
  ]);
  module.config([
    "$stateProvider",
    config.lookupManagementServiceProviderName,
    (
      $stateProvider: StateProvider,
      lookupManagementServiceProvider: LookupManagementServiceProvider
    ) => {
      lookupManagementServiceProvider.config = config;
      configureRoutes($stateProvider, config);
    },
  ]);
}

configureRoutes.$inject = ["$stateProvider"];
function configureRoutes(
  $stateProvider: StateProvider,
  config: LookupManagementServiceProviderConfig
) {
  const baseStateSref = config.baseStateSref;

  if (baseStateSref === undefined) {
    throw new Error(
      "Must set base state on lookup management service provider"
    );
  }

  $stateProvider
    .state(baseStateSref, {
      url: "/lookups",
      component: lookupManagementComponentName,
      resolve: {
        initialType: [
          "$stateParams",
          ($stateParams: StateParams) => $stateParams.type,
        ],
        lookupManagementService: [
          config.lookupManagementServiceName,
          (lookupManagementService: LookupManagementService) =>
            lookupManagementService,
        ],
      },
    })
    .state(`${baseStateSref}.detail`, {
      abstract: true,
      url: "/:type",
      params: {
        type: undefined,
      },
      resolve: {
        lookupType: [
          "$stateParams",
          ($stateParams: StateParams) => $stateParams.type,
        ],
        lookupManagementConfig: [
          "lookupType",
          config.lookupManagementServiceName,
          (
            lookupType: string,
            lookupManagementService: LookupManagementService
          ) => lookupManagementService.getLookupConfig(lookupType),
        ],
        gridConfig: [
          "lookupType",
          config.lookupManagementServiceName,
          (
            lookupType: string,
            lookupManagementService: LookupManagementService
          ) => {
            return lookupManagementService.getGridConfig(lookupType);
          },
        ],
      },
    })
    .state(`${baseStateSref}.detail.add`, getLookupEditState(true))
    .state(`${baseStateSref}.detail.edit`, getLookupEditState(false))
    .state(`${baseStateSref}.detail.grid`, {
      url: "",
      component: RecordGridViewComponentName,
      resolve: {
        gridDataSource: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.gridDataSource,
        ],
        gridSettings: [
          "gridSettingsService",
          "lookupType",
          (gridSettingsService: GridSettingsService, lookupType: string) =>
            gridSettingsService.getGridSettings(
              Symbol(`${baseStateSref}.grid.` + lookupType)
            ),
        ],
        columns: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.columns,
        ],
        defaultSort: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.defaultSort,
        ],
        nameSingular: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.nameSingular,
        ],
        namePlural: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.namePlural,
        ],
        showAddButton: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.showAddButton,
        ],
        showAddButtonPermission: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.showAddButtonPermission,
        ],
        onItemCommand: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.onItemCommand,
        ],
        onAddNew: [
          "$state",
          ($state: StateService) => () => {
            $state.go("^.add");
          },
        ],
      },
    })
    .state(`${baseStateSref}.detail.info`, {
      url: "/{id:int}",
      component: LookupDetailComponentName,
      params: {
        lookupItem: undefined,
      },
      resolve: {
        lookupItem: [
          "$stateParams",
          "lookUpID",
          "lookupManagementConfig",
          ServerUtilityName,
          (
            $stateParams: StateParams,
            lookUpID: number,
            lookupManagementConfig: LookupManagementConfig,
            serverUtility: ServerUtility
          ) => {
            const lookupItem = <number | undefined>$stateParams.lookupItem;
            if (lookupItem) return lookupItem;

            return serverUtility.querySingle({
              url: lookupManagementConfig.baseUrl,
              query: { Id: lookUpID },
            });
          },
        ],
        lookUpID: [
          "$stateParams",
          ($stateParams: StateParams) => $stateParams.id,
        ],
        editPermission: [
          "gridConfig",
          (gridConfig: GridConfiguration) => gridConfig.showAddButtonPermission,
        ],
        lookupReferences: [
          ServerUtilityName,
          "lookupManagementConfig",
          function (
            serverUtility: ServerUtility,
            lookupManagementConfig: LookupManagementConfig
          ) {
            return serverUtility
              .sendApiRequest<ServiceResponse<ReferenceRelationshipCollection>>(
                {
                  method: "get",
                  url: `${lookupManagementConfig.baseUrl}/references`,
                }
              )
              .then((r) => r.Result);
          },
        ],
      },
    });

  function getLookupEditState(isAddState: boolean) {
    const url = isAddState ? "/add" : "/edit/:id";
    return {
      url,
      component: FormViewComponentName,
      resolve: {
        formViewConfig: [
          FormViewServiceName,
          "lookupType",
          (formViewService: FormViewService, lookupType: string) =>
            formViewService.getFormViewConfig(lookupType),
        ],
        fields: [
          "formViewConfig",
          (formViewConfig: FormViewConfig) =>
            fieldHelpers.columnLayout(formViewConfig.fields, 4),
        ],
        model: [
          "$stateParams",
          "lookupManagementConfig",
          (
            $stateParams: StateParams,
            lookupManagementConfig: LookupManagementConfig
          ) => {
            const id = $stateParams.id;
            if (id === undefined) return undefined;
            return lookupManagementConfig.fetchFormModel(id);
          },
        ],
        title: [
          "lookupManagementConfig",
          (lookupManagementConfig: LookupManagementConfig) =>
            (isAddState ? "Add" : "Edit") +
            ` ${lookupManagementConfig.displayName}`,
        ],
        onSubmit: [
          "formViewConfig",
          "$state",
          "lookupType",
          (
            formViewConfig: FormViewConfig,
            $state: StateService,
            lookupType: string
          ) => [
            "formViewController",
            "model",
            (formViewController: FormViewController, model: any) =>
              formViewConfig
                .submitForm({ formViewController, model })
                .then((r) => {
                  $state.go("^.grid", {
                    type: lookupType,
                  });
                }),
          ],
        ],
        onCancel: [
          "$state",
          "lookupType",
          ($state: StateService, lookupType: string) => [
            () => {
              $state.go("^.grid", { type: lookupType });
            },
          ],
        ],
      },
    };
  }
}
